import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ExclamationTriangleIcon, ArrowPathIcon, TrashIcon,
  LinkIcon, CalendarIcon, CheckCircleIcon,
} from '@heroicons/react/24/outline';
import ErrorMessage from '../components/ErrorMessage';
import { bookmarkService } from '../services/bookmarkService';

// Load all failed bookmarks by exhausting pagination
async function loadAllFailed() {
  const items = [];
  let nextToken = null;
  do {
    const response = await bookmarkService.getBookmarks({ status: 'failed', limit: 100, nextToken });
    items.push(...(response.items || []));
    nextToken = response.nextToken || null;
  } while (nextToken);
  return items;
}

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

// ── Single broken-link row ────────────────────────────────────────────────────

const BrokenRow = ({ bookmark, onRetry, onDelete }) => (
  <div className="flex items-start gap-3 px-4 py-3">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
        {bookmark.title || bookmark.url}
      </p>
      <a
        href={bookmark.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 mt-0.5 min-w-0"
      >
        <LinkIcon className="h-3 w-3 flex-shrink-0" />
        <span className="truncate">{bookmark.url}</span>
      </a>
      {bookmark.errorMessage && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{bookmark.errorMessage}</p>
      )}
    </div>
    <div className="flex items-center gap-1 flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">
      <CalendarIcon className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{formatDate(bookmark.createdAt)}</span>
    </div>
    <div className="flex items-center gap-1 flex-shrink-0">
      {onRetry && (
        <button
          onClick={() => onRetry(bookmark.bookmarkId)}
          className="p-1.5 text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded transition-colors"
          title="Retry"
        >
          <ArrowPathIcon className="h-4 w-4" />
        </button>
      )}
      <button
        onClick={() => onDelete(bookmark.bookmarkId)}
        className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
        title="Delete"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </div>
  </div>
);

// ── Section (dead_link or transient) ─────────────────────────────────────────

const Section = ({ title, description, accentClass, items, bulkLabel, onBulkAction, bulkDestructive, onRetry, onDelete }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);

  const handleBulk = async () => {
    if (bulkDestructive && !confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setConfirmDelete(false);
    setBulkRunning(true);
    setBulkProgress({ done: 0, total: items.length });
    let done = 0;
    for (const bm of items) {
      await onBulkAction(bm.bookmarkId).catch(() => {});
      done++;
      setBulkProgress({ done, total: items.length });
      if (done < items.length) await new Promise(r => setTimeout(r, 150));
    }
    setBulkRunning(false);
    setBulkProgress(null);
  };

  if (items.length === 0) return null;

  return (
    <div className={`rounded-xl border ${accentClass} overflow-hidden`}>
      {/* Section header */}
      <div className="px-4 py-3 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {confirmDelete && (
            <span className="text-xs text-red-700 dark:text-red-400 font-medium">Are you sure?</span>
          )}
          {confirmDelete && (
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-2 py-1 rounded transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleBulk}
            disabled={bulkRunning}
            className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
              bulkDestructive
                ? confirmDelete
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50 border border-amber-200 dark:border-amber-700'
            }`}
          >
            {bulkDestructive
              ? <TrashIcon className="h-3.5 w-3.5" />
              : <ArrowPathIcon className={`h-3.5 w-3.5 ${bulkRunning ? 'animate-spin' : ''}`} />
            }
            {bulkRunning && bulkProgress
              ? `${bulkProgress.done}/${bulkProgress.total}`
              : confirmDelete
                ? `Delete all ${items.length}`
                : bulkLabel}
          </button>
        </div>
      </div>

      {/* Rows */}
      <div className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
        {items.map(bm => (
          <BrokenRow
            key={bm.bookmarkId}
            bookmark={bm}
            onRetry={onRetry}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const BrokenLinks = () => {
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setBookmarks(await loadAllFailed());
    } catch (err) {
      setError(err.message || 'Failed to load broken links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = useCallback(async (bookmarkId) => {
    await bookmarkService.deleteBookmark(bookmarkId);
    setBookmarks(prev => prev.filter(b => b.bookmarkId !== bookmarkId));
  }, []);

  const handleRetry = useCallback(async (bookmarkId) => {
    await bookmarkService.retryBookmark(bookmarkId);
    setBookmarks(prev => prev.filter(b => b.bookmarkId !== bookmarkId));
  }, []);

  const deadLinks = bookmarks.filter(b => b.failureType === 'dead_link');
  const transient = bookmarks.filter(b => b.failureType !== 'dead_link');

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Broken Links</h1>
          {bookmarks.length > 0 && (
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {bookmarks.length} bookmark{bookmarks.length !== 1 ? 's' : ''} failed to process
            </p>
          )}
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 px-3 py-1.5 rounded-md transition-colors"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {error && <ErrorMessage message={error} onRetry={load} />}

      {bookmarks.length === 0 && !error && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <CheckCircleIcon className="h-12 w-12 text-green-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No broken links</h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm">All your bookmarks processed successfully.</p>
          <Link to="/bookmarks" className="mt-4 inline-block text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700">
            Back to bookmarks →
          </Link>
        </div>
      )}

      {/* Dead links — permanently unavailable */}
      <Section
        title={`Permanently unavailable (${deadLinks.length})`}
        description="Page not found, domain gone, or SSL failure. These links are unlikely to come back."
        accentClass="border-red-200 bg-red-50"
        items={deadLinks}
        bulkLabel={`Delete all ${deadLinks.length}`}
        bulkDestructive
        onBulkAction={handleDelete}
        onRetry={handleRetry}
        onDelete={handleDelete}
      />

      {/* Transient failures — worth retrying */}
      <Section
        title={`Processing failed (${transient.length})`}
        description="Timeout, server error, or unknown issue. These may succeed on retry."
        accentClass="border-amber-200 bg-amber-50"
        items={transient}
        bulkLabel={`Retry all ${transient.length}`}
        bulkDestructive={false}
        onBulkAction={handleRetry}
        onRetry={handleRetry}
        onDelete={handleDelete}
      />

      {bookmarks.length > 0 && (
        <div className="flex items-center justify-between pt-2">
          <Link to="/bookmarks" className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
            ← Back to all bookmarks
          </Link>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            <ExclamationTriangleIcon className="h-3.5 w-3.5 inline mr-1" />
            Deleted bookmarks cannot be recovered
          </p>
        </div>
      )}
    </div>
  );
};

export default BrokenLinks;
